<template>
  <ul class="stepper">
    <li v-for="(step, index) in steps" :key="index" :class="{ 'active': useKey ? step.key === currentStep : index === currentStep }" class="step">
      <span class="step-line" />
      <span class="step-label">{{ step.label | capitalize }}</span>
      <span class="step-indicator" />
    </li>
  </ul>
</template>

<script>
// ⚠️ When using vee-validate, be sure to use the attribute "key" in pages on inputs, select...
// Vue will reuse form components to optimize rendering but it will mess up the validator
// https://vuejs.org/v2/guide/conditional.html#Controlling-Reusable-Elements-with-key
import { isNumber, findIndex } from 'lodash-es'

export default {
  model: {
    prop: 'currentStep',
    event: 'change'
  },

  props: {
    // Current active step, used with v-model
    currentStep: {
      type: [Number, String],
      default: 0,
      required: true
    },
    // List of steps definition:
    // label: the label of the step (mandatory)
    // key: the id of the step (mandatory if useKey is true)
    steps: {
      type: Array,
      default: () => []
    },
    // On next callback
    // return true to continue to next page
    // return false to stop
    onNext: {
      type: Function,
      default: null
    },
    // On previous callback
    // return true to continue to next page
    // return false to stop
    onPrevious: {
      type: Function,
      default: null
    },
    // Use step keys instead of indexes
    // Usefull if the steps are dynamically changed
    useKey: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    currentStepIndex () {
      if (isNumber(this.currentStep)) return this.currentStep
      return findIndex(this.steps, step => step.key === this.currentStep)
    }
  },

  methods: {
    // Use $ref to call this next function to check the callback and continue
    async next () {
      if (typeof this.onNext === 'function') {
        const shouldContinue = await this.onNext(this.currentStep)
        if (!shouldContinue) return
      }

      if (this.currentStepIndex < this.steps.length - 1) {
        if (this.useKey) this.$emit('change', this.steps[this.currentStepIndex + 1].key)
        else this.$emit('change', this.currentStep + 1)
      }
    },

    // Use $ref to call this previous function to check the callback and continue
    async previous () {
      if (typeof this.onPrevious === 'function') {
        const shouldContinue = await this.onPrevious(this.currentStep)
        if (!shouldContinue) return
      }

      if (this.currentStepIndex > 0) {
        if (this.useKey) this.$emit('change', this.steps[this.currentStepIndex - 1].key)
        else this.$emit('change', this.currentStep - 1)
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  .stepper {
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
    padding: 0;
    margin-bottom: 3em;
    text-align: justify;
    -ms-text-justify: distribute-all-lines;
    text-justify: distribute-all-lines;
    list-style-type: none;

    .step {
      position: relative;
      display: inline-block;
      flex: 1;
      height: 50px;
      text-align: center;
      vertical-align: bottom;

      .step-label {
        font-weight: bold;
        color: $gray-500;
      }

      .step-indicator {
        position: absolute;
        bottom: 2px;
        left: 50%;
        z-index: 1;
        box-sizing: content-box;
        display: block;
        width: 16px;
        height: 16px;
        margin-left: -10px;
        background-color: $primary;
        border: 3px solid #fff;
        border-radius: 50%;
      }

      &:not(:first-child) .step-line {
        position: absolute;
        bottom: 12px;
        left: -50%;
        width: 100%;
        height: 3px;
        background-color: $gray-500;
      }

      &.active {
        .step-label {
          color: $secondary;
        }

        .step-indicator {
          background-color: $secondary;
        }
      }
    }
  }
</style>
